import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { MdSearch } from 'react-icons/md';
import { Empty, Select } from '@vyaguta/vyaguta-ui';

import useQuery from 'hooks/useQuery';
import useTitle from 'hooks/useTitle';
import { noticeBoardQueryOptions, useFetchNoticeBoardQuery } from 'hooks/query/useNoticeQueries';

import { DEFAULT_PAGE } from 'constants/page';
import { YEAR_OPTIONS } from 'constants/options';
import { DEFAULT_PAGE_SIZE } from 'constants/appConstants';

import { getFormattedDate } from 'utils/date';
import { getDefaultYearFilter } from 'utils/notice';

import NoticeBoardCard from './components/NoticeBoardCard';

import type { Notice } from 'interface/notices';
import type { QueryObject, RouterProps } from 'interface/queryParam';

type NoticeBoardQuery = QueryObject & {
  year?: string;
  q?: string;
};

type NoticeItem = Notice & {
  id: number;
};

type YearOption = {
  value: number;
  label: string;
};

type YearSelectProps = {
  isSearchable?: boolean;
  autoResize?: boolean;
  options: YearOption[];
  value: YearOption | null;
  onChange: (option: YearOption | null) => void;
  placeholder?: string;
};

const YearSelect = Select as unknown as React.ComponentType<YearSelectProps>;

const toYearQueryValue = (year: number | string) => String(year);

type Props = Partial<RouterProps>;

const NoticeBoard: React.FC<Props> = (props) => {
  useTitle('Noticeboard');

  const currentYear = moment().year();
  const currentDate = getFormattedDate();

  const [query, setQuery] = useQuery<Props>(props);
  const typedQuery = query as NoticeBoardQuery;
  const hasAppliedYearFilter = typedQuery.year != null && Number(typedQuery.year) !== currentYear;
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const isFilterApplied = Boolean(typedQuery.q) || hasAppliedYearFilter;
  const [filters, setFilters] = useState<NoticeBoardQuery>({ ...typedQuery });
  const [searchQuery, setSearchQuery] = useState(typedQuery.q ?? '');

  const baseFilters = useMemo(
    () => ({
      page: DEFAULT_PAGE,
      size: DEFAULT_PAGE_SIZE,
      year: typedQuery.year ? toYearQueryValue(typedQuery.year) : undefined,
      q: typedQuery.q,
    }),
    [typedQuery.q, typedQuery.year]
  );

  const isQueryEnabled = Boolean(typedQuery.year);

  const firstPageQuery = useFetchNoticeBoardQuery(baseFilters, isQueryEnabled);

  const totalPages = useMemo(() => {
    if (!firstPageQuery.data?.meta) {
      return 0;
    }

    const { total } = firstPageQuery.data.meta;
    return Math.ceil(total / DEFAULT_PAGE_SIZE);
  }, [firstPageQuery.data]);

  const additionalPageQueries = useQueries({
    queries: Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) => ({
      ...noticeBoardQueryOptions({
        ...baseFilters,
        page: index + 2,
      }),
      enabled: isQueryEnabled && firstPageQuery.isSuccess && totalPages > 1,
    })),
  });

  const isLoading =
    firstPageQuery.isLoading || (totalPages > 1 && additionalPageQueries.some((pageQuery) => pageQuery.isLoading));

  const noticeCount = firstPageQuery.data?.meta?.total;

  const noticeList = useMemo(() => {
    const combinedNotices: NoticeItem[] = [];

    if (firstPageQuery.data?.data) {
      combinedNotices.push(...(firstPageQuery.data.data as NoticeItem[]));
    }

    additionalPageQueries.forEach((pageQuery) => {
      if (pageQuery.data?.data) {
        combinedNotices.push(...(pageQuery.data.data as NoticeItem[]));
      }
    });

    return combinedNotices;
  }, [additionalPageQueries, firstPageQuery.data]);

  const applyFilter = (filter: NoticeBoardQuery) => {
    setQuery({ ...typedQuery, ...filter });
  };

  const handleYearChange = (option: YearOption | null) => {
    if (!option) {
      return;
    }

    setFilters((previousFilters) => ({ ...previousFilters, year: toYearQueryValue(option.value) }));
    setIsFilterChanged(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const textValue = event.target.value.trimStart();

    setSearchQuery(textValue);
    setFilters((previousFilters) => ({ ...previousFilters, q: textValue }));
    setIsFilterChanged(true);
  };

  const handleApplyFilter = () => {
    if (!isFilterChanged) {
      return;
    }

    applyFilter(filters);
    setIsFilterChanged(false);
  };

  const handleReset = () => {
    if (!isFilterApplied) {
      return;
    }

    setFilters({});
    setSearchQuery('');
    applyFilter({ year: toYearQueryValue(YEAR_OPTIONS[1].value), q: '' });
  };

  useEffect(() => {
    if (!typedQuery.year) {
      setQuery(getDefaultYearFilter());
    }
  }, [setQuery, typedQuery.year]);

  const currentNoticeList = useMemo(
    () => noticeList.filter((notice) => currentDate >= notice.startDate && currentDate <= notice.endDate),
    [currentDate, noticeList]
  );

  const pastNoticeList = useMemo(
    () => noticeList.filter((notice) => !(currentDate >= notice.startDate && currentDate <= notice.endDate)),
    [currentDate, noticeList]
  );

  const sortedNoticeList = useMemo(
    () => [...currentNoticeList, ...pastNoticeList],
    [currentNoticeList, pastNoticeList]
  );

  const yearSelectValue: YearOption = YEAR_OPTIONS.find((opt) => opt.value === Number(filters.year)) ?? YEAR_OPTIONS[1];

  return (
    <main>
      <div className="container">
        <div className={classNames('full-scope-card', 'mbpx-0')}>
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex">
              <h3 className="table-title">Noticeboard</h3>
            </div>
            <div className="d-flex align-items-center notice-board-filter custom-scroll-bar">
              <div className="search-wrapper notice-search d-flex align-items-center">
                <MdSearch className="search-icon" />
                <input
                  className="search-bar notice-search__input"
                  value={searchQuery}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleApplyFilter();
                    }
                  }}
                  type="text"
                  placeholder="Search notice"
                  onChange={handleSearchChange}
                />
              </div>
              <div className="hpx-45 mlpx-5">
                <YearSelect
                  isSearchable={true}
                  options={YEAR_OPTIONS}
                  autoResize={true}
                  onChange={handleYearChange}
                  value={yearSelectValue}
                  placeholder="Year"
                />
              </div>
              <div className="title-bar__filter-buttons d-flex align-items-center">
                <button
                  className={classNames('button m-10 ml-24 wpx-96', {
                    'button--primary': !isFilterChanged,
                    'button--secondary': isFilterChanged,
                  })}
                  onClick={handleApplyFilter}
                >
                  Apply Filter
                </button>

                <button
                  className={classNames('button__reset', {
                    'button__reset--active': isFilterApplied,
                  })}
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className={classNames('mtpx-24 mbpx-10 notice-board-container', {
            'notice-board-container--empty': !noticeCount && !isLoading,
          })}
        >
          {isLoading ? (
            <SkeletonTheme baseColor="#e9edf0" highlightColor="#F0F3F5">
              <Skeleton width={152} height={16} />
              <div className="full-scope-card__content mtpx-16">
                <div>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <Skeleton className="notice-board-card-loader" key={index} />
                  ))}
                </div>
              </div>
            </SkeletonTheme>
          ) : noticeCount ? (
            <>
              <span className="notice-board__count">{`Showing ${noticeCount} notices of ${typedQuery.year}`}</span>
              <div className="full-scope-card__content mtpx-16">
                {sortedNoticeList.map((noticeItem) => (
                  <NoticeBoardCard notice={noticeItem} key={noticeItem.id} />
                ))}
              </div>
            </>
          ) : (
            <Empty message="Notices not found." />
          )}
        </div>
      </div>
    </main>
  );
};

export default NoticeBoard;
